import { FC } from 'react';

import { toast } from 'react-toastify';

import { useStopWalletMutation, type IWallet, type IWalletClickhouse } from '@entities/wallets';
import ModalConfirm from '@features/modal-confirm';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import { IconButton } from '@mui/material';
import { useModal } from '@shared/components/Modal';
import { isApiError } from '@shared/types/fetch-data';

interface IProps {
  wallet: IWallet | IWalletClickhouse;
  refetch?: () => void;
}

export const StopTrafficBtn: FC<IProps> = ({ wallet, refetch }) => {
  const [stopWallet, { isLoading, isError }] = useStopWalletMutation();
  const { openModal, closeModal } = useModal();

  const disabled = wallet.status !== 'live' || isLoading;
  const activeColor = '#FFBDAD';
  const disabledColor = '#97A0AF';

  const handleClick = () => {
    openModal(
      <ModalConfirm
        title='Are you sure?'
        message='This will deactivate wallet and expire all existing sessions.'
        onConfirm={handleStopTraffic}
        onCancel={closeModal}
      />,
    );
  };

  const handleStopTraffic = async () => {
    try {
      // Convert IWallet to IWalletClickhouse format for the API call
      const walletForApi =
        'wallet_name' in wallet
          ? wallet
          : ({
              id: wallet.id,
              wallet_name: wallet.name,
              status: wallet.status,
              // Add other required fields for the API
            } as IWalletClickhouse);

      const result = await stopWallet(walletForApi);
      if ('data' in result) {
        toast.success(result.data.message);
        closeModal();
        refetch?.();
      }
    } catch (error) {
      if (isError && error && isApiError(error)) {
        toast.error(error.data.message);
      }
    }
  };

  return (
    <IconButton
      disabled={disabled}
      sx={{ '&.Mui-disabled': { opacity: 1 }, margin: 'auto' }}
      aria-label='Stop traffic'
      title='Stop traffic'
      onClick={handleClick}
    >
      <DoNotDisturbOnIcon sx={{ color: disabled ? disabledColor : activeColor }} />
    </IconButton>
  );
};
